import { useState } from "react";
import { useTranslation } from "react-i18next";

interface DriftItem {
  key: string;
  label: string;
  actualCents: number;
  targetCents?: number;
  driftCents?: number;
  driftPp?: number;
  underwater?: true;
}

interface Props {
  items: DriftItem[];
  totalCents: number;
  formatEur: (cents: number) => string;
  onRowClick?: (key: string) => void;
}

export function DriftTableCompact({ items, totalCents, formatEur, onRowClick }: Props) {
  const { t } = useTranslation();
  const [openItem, setOpenItem] = useState<DriftItem | null>(null);

  const close = () => setOpenItem(null);

  const handleClick = (item: DriftItem) => {
    if (onRowClick) onRowClick(item.key);
    else setOpenItem(item);
  };

  return (
    <>
      <div className="fv-card overflow-hidden">
        <div
          className="grid grid-cols-[1fr_64px_72px] text-xs font-semibold px-3 py-2 border-b sticky top-0 z-10"
          style={{ borderColor: "var(--border)", background: "var(--card)", color: "var(--muted)" }}
        >
          <span>{t("allocation.column.class", { defaultValue: "Klasse" })}</span>
          <span className="text-right">
            {t("allocation.column.ist_pct", { defaultValue: "Ist" })}
          </span>
          <span className="text-right">
            {t("allocation.column.drift_combined", { defaultValue: "Drift" })}
          </span>
        </div>
        {items.map((item) => {
          const istPct = totalCents > 0 ? (item.actualCents / totalCents) * 100 : 0;
          const driftPp = item.driftPp ?? null;
          const driftColor =
            driftPp === null ? "var(--muted)" : driftPp >= 0 ? "var(--pos)" : "var(--neg)";
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleClick(item)}
              className="w-full grid grid-cols-[1fr_64px_72px] text-sm px-3 py-3 border-b min-h-[48px] items-center text-left active:opacity-70"
              style={{ borderColor: "var(--border)" }}
            >
              <span className="font-medium truncate">
                {item.label}
                {item.underwater && (
                  <span
                    className="ml-2 text-xs px-1.5 py-0.5 rounded"
                    style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
                  >
                    {t("allocation.underwater_badge", { defaultValue: "Underwater" })}
                  </span>
                )}
              </span>
              <span className="text-right num">{istPct.toFixed(1)} %</span>
              <span className="text-right num font-medium" style={{ color: driftColor }}>
                {driftPp === null ? "—" : `${driftPp >= 0 ? "+" : ""}${driftPp.toFixed(1)} pp`}
              </span>
            </button>
          );
        })}
      </div>
      {openItem && (
        <>
          <div
            className="fixed inset-0 z-40"
            style={{ background: "rgba(0,0,0,0.5)" }}
            onClick={close}
            onKeyDown={(e) => {
              if (e.key === "Escape") close();
            }}
            role="presentation"
            aria-hidden="true"
          />
          <div
            className="fixed left-4 right-4 top-1/2 -translate-y-1/2 z-50 rounded-2xl p-4 fv-card"
            style={{ background: "var(--card)", maxHeight: "70vh", overflowY: "auto" }}
          >
            <h3 className="text-lg font-semibold mb-3">{openItem.label}</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt style={{ color: "var(--muted)" }}>
                  {t("allocation.column.ist_eur", { defaultValue: "Ist €" })}
                </dt>
                <dd className="num">{formatEur(openItem.actualCents)}</dd>
              </div>
              <div className="flex justify-between">
                <dt style={{ color: "var(--muted)" }}>
                  {t("allocation.column.ist_pct", { defaultValue: "Ist %" })}
                </dt>
                <dd className="num">
                  {(totalCents > 0 ? (openItem.actualCents / totalCents) * 100 : 0).toFixed(1)} %
                </dd>
              </div>
              <div className="flex justify-between">
                <dt style={{ color: "var(--muted)" }}>
                  {t("allocation.column.soll_eur", { defaultValue: "Soll €" })}
                </dt>
                <dd className="num">
                  {openItem.targetCents !== undefined ? formatEur(openItem.targetCents) : "—"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt style={{ color: "var(--muted)" }}>
                  {t("allocation.column.drift_eur", { defaultValue: "Drift €" })}
                </dt>
                <dd
                  className="num font-medium"
                  style={{
                    color:
                      openItem.driftCents === undefined
                        ? "var(--muted)"
                        : openItem.driftCents >= 0
                          ? "var(--pos)"
                          : "var(--neg)",
                  }}
                >
                  {openItem.driftCents !== undefined
                    ? `${openItem.driftCents >= 0 ? "+" : ""}${formatEur(openItem.driftCents)}`
                    : "—"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt style={{ color: "var(--muted)" }}>
                  {t("allocation.column.drift_pp", { defaultValue: "Drift pp" })}
                </dt>
                <dd
                  className="num font-medium"
                  style={{
                    color:
                      openItem.driftPp === undefined
                        ? "var(--muted)"
                        : openItem.driftPp >= 0
                          ? "var(--pos)"
                          : "var(--neg)",
                  }}
                >
                  {openItem.driftPp !== undefined
                    ? `${openItem.driftPp >= 0 ? "+" : ""}${openItem.driftPp.toFixed(1)} pp`
                    : "—"}
                </dd>
              </div>
            </dl>
            <button
              type="button"
              onClick={close}
              className="w-full mt-4 px-3 py-2 text-sm font-medium rounded-md border min-h-[44px]"
              style={{ borderColor: "var(--border)" }}
            >
              {t("common.close", { defaultValue: "Schließen" })}
            </button>
          </div>
        </>
      )}
    </>
  );
}
